import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { PropertyService } from '../../properties/service/property.service';
import { debounceTime, distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'app-create-amenity',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    ToastModule,
  ],
  templateUrl: './create-amenity.component.html',
  styleUrls: ['./create-amenity.component.scss'],
  providers: [MessageService],
})
export class CreateAmenityComponent implements OnInit, OnChanges {
  @Input() amenityToEdit: any = null; // Input for editing an amenity
  /** When embedded (e.g. amenities dialog), parent passes the current property id from JWT. */
  @Input() hostPropertyId: string | null = null;
  @Output() amenitySaved = new EventEmitter<void>();

  isEdit = false;
  nameAlreadyExists = false;
  amenityForm: FormGroup;
  amenityCategories: any[] = [];
  isEditing: boolean = false;
  userInfo: any = null;
  /** Resolved from hostPropertyId (preferred) or JWT / getUserInfo */
  propertyId: string = '';

  get propertyIdForSave(): string {
    const fromHost = this.hostPropertyId?.trim() ?? '';
    const fromToken = this.propertyId?.trim() ?? '';
    return fromHost || fromToken;
  }
  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private router: Router,
    private messageService: MessageService,
    private propertyService: PropertyService
  ) {
    this.amenityForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      amenity_category_id: ['', Validators.required],
      status: [true, Validators.required],
    });
  }

  ngOnInit() {
    this.getAmenityCategories();

    if (this.amenityToEdit) {
      this.isEditing = true;
      this.populateForm(this.amenityToEdit);
    }

    this.amenityForm.get('name')?.valueChanges
      .pipe(
        debounceTime(300), // wait for user to stop typing
        distinctUntilChanged()
      )
      .subscribe((value) => {
        if (!this.isEditing && value && value.trim()) {
          this.checkAmenityName(value.trim());
        }
    });

    this.applyHostPropertyId();
    this.propertyService.getUserInfo().subscribe((user) => {
      this.userInfo = user;
      const fromToken = user?.property_id != null ? String(user.property_id).trim() : '';
      if (!this.hostPropertyId?.trim() && fromToken) {
        this.propertyId = fromToken;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['hostPropertyId']) {
      this.applyHostPropertyId();
    }
    if (changes['amenityToEdit'] && changes['amenityToEdit'].currentValue) {
      this.isEditing = true;
      this.populateForm(changes['amenityToEdit'].currentValue);
    }
  }

  private applyHostPropertyId(): void {
    const h = this.hostPropertyId?.trim();
    if (h) {
      this.propertyId = h;
    }
  }

  getAmenityCategories() {
    this.propertyService.getAmenityCategories().subscribe((res) => {
      if (res) {
        this.amenityCategories = res;
      }
    });
  }

  checkAmenityName(name: string) {
    const currentAmenityId = this.isEditing ? this.amenityToEdit._id : null;
    this.propertyService.getAmenitiesByName(name, this.propertyId).subscribe((res) => {
      if (res && res.length > 0) {
        let duplicate = true;
        if (currentAmenityId) { // If editing, ignore same amenity record
          duplicate = res.some((a: { _id: string }) => a._id !== currentAmenityId);
        }

        this.nameAlreadyExists = duplicate;        
        //this.amenityForm.get('name')?.setValue(''); // clear field
      } else {
        this.nameAlreadyExists = false;
      }
    });
  }

  populateForm(amenity: any) {
    this.amenityForm.patchValue({
      name: amenity.name,
      description: amenity.description,
      amenity_category_id: amenity.amenity_category_id,
      status: amenity.status,
    });
  }

  saveAmenity() {
    if (this.amenityForm.valid) {
      const effectivePropertyId = this.propertyIdForSave;
      if (!effectivePropertyId) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Missing property',
          detail: 'A property id is required to save an amenity. Sign in with a property context or open this screen from the property amenities page.',
        });
        return;
      }

      const amenityData = { ...this.amenityForm.value, property_id: effectivePropertyId };

      const selectedCategory = this.amenityCategories.find(
        (category) => category._id === amenityData.amenity_category_id
      );

      if (selectedCategory) {
        amenityData.amenity_category_name = selectedCategory.name;
      }

      if (this.isEditing) {
        this.propertyService.updateAmenity(this.amenityToEdit._id, amenityData).subscribe({
          next: () => {
            // this.messageService.add({
            //   severity: 'success',
            //   summary: 'Success',
            //   detail: 'Amenity updated successfully!',
            // });
            this.amenityForm.reset();
            this.amenitySaved.emit();
            this.isEditing = false;
          },
          error: (err: any) => {
            console.error('Error updating amenity:', err);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to update amenity.',
            });
          },
        });
      } else {
        this.propertyService.saveAmenity(amenityData).subscribe({
          next: () => {
            // this.messageService.add({
            //   severity: 'success',
            //   summary: 'Success',
            //   detail: 'Amenity created successfully!',
            // });
            this.amenityForm.reset();
            this.amenitySaved.emit();
          },
          error: (err) => {
            console.error('Error creating amenity:', err);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to create amenity.',
            });
          },
        });
      }
    }
  }
}
